import { getCurrentLocaleName } from '../utils/_i18n';
import { parseISODate, stringifyISODate } from '../utils/_date';
import { collectRefs } from '../utils/_webComponents';

const dateTimeFormat = new Intl.DateTimeFormat(getCurrentLocaleName(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
});

class mDatePickerField extends HTMLDivElement {
    constructor() {
        super();
        collectRefs.call(this);

        this.date = new Date();

        // Create/get text (read only) input
        this.textInput = this.querySelector('input[type="text"]');
        if (!this.textInput) {
            this.textInput = document.createElement('input');
            this.prepend(this.textInput);
        }
        this.textInput.setAttribute('role', 'button');
        this.textInput.setAttribute('aria-haspopup', 'true');
        this.textInput.setAttribute('aria-expanded', 'false');
        // Avoid using readonly attribute since it skips the "required" validity check
        this.textInput.setAttribute('type', 'text');
        this.textInput.addEventListener('click', this.onTextInputClick.bind(this));
        this.textInput.addEventListener('keydown', this.onTextInputKeydown.bind(this));

        // Create/get hidden input
        this.hiddenInput = this.querySelector('input[type="hidden"]');
        if (!this.hiddenInput) {
            this.hiddenInput = document.createElement('input');
            this.textInput.after(this.hiddenInput);
        }
        this.hiddenInput.setAttribute('type', 'hidden');

        // Set value
        if (this.hiddenInput.value) {
            this.setDate(parseISODate(this.hiddenInput.value), false);
        } else if (this.textInput.value) {
            this.setDate(parseISODate(this.textInput.value), true);
        } else {
            this.setDate(undefined, true);
        }

        // Create date picker dialog
        this.datePickerMenu = this.querySelector('[is="m-date-picker-menu"]');
        this.datePickerMenu.addEventListener('close', this.onDatePickerDialogClose.bind(this));
        this.closeDatePicker();
        this.datePicker = this.datePickerMenu.querySelector('[is="m-date-picker"]');
        this.datePicker.addEventListener('change', this.onDatePickerChange.bind(this));
    }

    connectedCallback() {}

    disconnectedCallback() {}

    showDatePicker() {
        this.datePickerMenu.classList.add('open');
        this.textInput.setAttribute('aria-expanded', 'true');
    }

    closeDatePicker() {
        this.textInput.setAttribute('aria-expanded', 'false');
        this.datePickerMenu.classList.remove('open');
    }

    setDate(newDate, noEvent) {
        const oldDate = this.date;
        this.date = newDate;

        const field = this.closest('.form-field');
        if (!(newDate instanceof Date) || Number.isNaN(this.date.valueOf())) {
            this.textInput.value = '';
            this.hiddenInput.value = '';
            if (field) {
                field.classList.remove('is-active');
            }
        } else {
            this.textInput.value = dateTimeFormat.format(this.date);
            this.hiddenInput.value = stringifyISODate(this.date);
            if (field) {
                field.classList.add('is-active');
            }
        }
        if (!noEvent && oldDate !== this.date) {
            this.textInput.dispatchEvent(new Event('input')); // Needed to reset m-ajax-form validity
            this.textInput.dispatchEvent(new Event('change'));
            this.hiddenInput.dispatchEvent(new Event('change'));
        }
    }

    focusDatePicker() {
        let focusEl = this.datePicker.querySelector('td[tabIndex="0"]');
        if (!focusEl) {
            focusEl = this.datePicker.querySelector('button');
        }
        if (!focusEl) {
            focusEl = this.datePicker;
        }
        focusEl.focus();
    }

    onTextInputClick() {
        this.showDatePicker();
        this.focusDatePicker();
    }

    onTextInputKeydown(event) {
        event.preventDefault(); // Prevent text input edit, rendering it "readonly"
        if (event.key === 'Enter' || event.key === ' ') {
            this.showDatePicker();
            this.focusDatePicker();
        }
    }

    onDatePickerChange() {
        this.setDate(parseISODate(this.datePicker.value));
        this.closeDatePicker();
    }

    onDatePickerDialogClose() {
        this.closeDatePicker();
        this.textInput.focus();
    }
}

customElements.define('m-date-picker-field', mDatePickerField, {
    extends: 'div'
});
